import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "../components/ui/sonner";
import { useApp } from "../context/AppContext";
import { PHASES } from "../data/constants";
import { setPhaseStandards } from "../lib/schedule";
import { SuggestBadge, SuggestBar } from "../components/phase/PhaseSuggest";
import { ArrowLeft, Save, RotateCcw, Copy, Loader2, Ruler, Lock, Sparkles } from "lucide-react";

const cloneCfg = (data) => ({ default: [...data.default], byType: Object.fromEntries(Object.entries(data.byType || {}).map(([k, v]) => [k, [...v]])) });

export default function PhaseStandards() {
  const nav = useNavigate();
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const canEdit = ["admin", "eng_manager", "ceo"].includes(user.role);
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSug, setShowSug] = useState(false);
  const [months, setMonths] = useState(6);
  const [sug, setSug] = useState(null);
  const [sugBusy, setSugBusy] = useState(false);
  const load = () => api.get("/phase-standards").then((r) => { setCfg(r.data); setDraft(cloneCfg(r.data)); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const loadSug = useCallback(() => {
    setSugBusy(true);
    api.get("/phase-standards/suggest", { params: { months } }).then((r) => setSug(r.data)).catch((e) => toast.error(e?.detailText || "Failed")).finally(() => setSugBusy(false));
  }, [api, months]);
  useEffect(() => { if (showSug) loadSug(); }, [showSug, loadSug]);
  const dirty = useMemo(() => cfg && draft && JSON.stringify({ d: cfg.default, b: cfg.byType }) !== JSON.stringify({ d: draft.default, b: draft.byType }), [cfg, draft]);
  if (!cfg || !draft) return <div className="p-6 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-1" />{zh ? "加载中…" : "Loading…"}</div>;

  const arrOf = (d, type) => (type === "default" ? d.default : (d.byType[type] || d.default));
  const setCell = (type, i, v) => {
    const n = Math.max(1, Math.min(365, parseInt(v || "0", 10) || 0));
    setDraft((d) => {
      if (type === "default") { const a = [...d.default]; a[i] = n; return { ...d, default: a }; }
      const a = [...(d.byType[type] || d.default)]; a[i] = n;
      return { ...d, byType: { ...d.byType, [type]: a } };
    });
  };
  const followDefault = (type) => setDraft((d) => { const b = { ...d.byType }; delete b[type]; return { ...d, byType: b }; });
  const copyDefault = (type) => setDraft((d) => ({ ...d, byType: { ...d.byType, [type]: [...d.default] } }));
  const sugRows = (type) => (sug ? (type === "default" ? sug.default : sug.byType?.[type]) : null) || [];
  const adoptRow = (type) => setDraft((d) => {
    const a = [...arrOf(d, type)];
    sugRows(type).forEach((s) => { if (s.suggested != null) a[s.idx] = s.suggested; });
    return type === "default" ? { ...d, default: a } : { ...d, byType: { ...d.byType, [type]: a } };
  });
  const adoptable = sug ? ["default", ...cfg.types.map((t) => t.key)].reduce((n, k) => n + sugRows(k).filter((s) => s.suggested != null && s.suggested !== arrOf(draft, k)[s.idx]).length, 0) : 0;
  const adoptAll = () => { ["default", ...cfg.types.map((t) => t.key)].forEach((k) => { if (sugRows(k).some((s) => s.suggested != null)) adoptRow(k); }); toast.success(zh ? "已填入建议值，请检查后保存" : "Suggestions applied — review and save"); };
  const save = async () => {
    setBusy(true);
    try {
      const byType = {};
      cfg.types.forEach((t) => { byType[t.key] = draft.byType[t.key] || null; });
      const { data } = await api.put("/phase-standards", { default: draft.default, byType });
      setCfg(data); setPhaseStandards(data); setDraft(cloneCfg(data));
      toast.success(zh ? "标准工期已保存，看板与周报即时生效" : "Saved — board and reports now use the new standards");
    } catch (e) { toast.error(e?.detailText || "Failed"); } finally { setBusy(false); }
  };
  const resetAll = async () => {
    if (!window.confirm(zh ? `恢复系统默认（所有类型统一 ${cfg.systemDefault.join("/")} 天）？` : "Reset everything to system defaults?")) return;
    setBusy(true);
    try { const { data } = await api.put("/phase-standards", { resetAll: true }); setCfg(data); setPhaseStandards(data); setDraft(cloneCfg(data)); toast.success(zh ? "已恢复默认" : "Reset"); }
    catch (e) { toast.error(e?.detailText || "Failed"); } finally { setBusy(false); }
  };
  const total = (arr) => arr.reduce((s, x) => s + x, 0);
  const rows = [{ key: "default", zh: "默认（未单列的类型）", en: "Default (other types)" }, ...cfg.types];

  return (
    <div className="space-y-4" data-testid="phase-standards">
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <button onClick={() => nav(-1)} className="text-slate-400 hover:text-slate-600" data-testid="ps-back"><ArrowLeft className="h-4 w-4" /></button>
            <Ruler className="h-5 w-5 text-brand-500" />
            <h1 className="text-lg font-black text-slate-900">{zh ? "阶段标准工期（超期口径）" : "Phase standard durations (overdue rule)"}</h1>
            {!canEdit && <span className="flex items-center gap-1 text-[11px] text-slate-400"><Lock className="h-3 w-3" />{zh ? "仅工程经理 / 管理员可改" : "Manager/Admin only"}</span>}
          </div>
          <p className="text-xs text-slate-400 mt-1">
            {zh ? "按项目类型设置各阶段标准天数：阶段停留 > 标准 = 预警，> 2 × 标准 = 超期；项目看板、分公司周报、催办均按此口径。" : "Per project type: stayed > standard = warning, > 2× standard = overdue; used by the board, branch reports and nudges."}
            {cfg.updatedAt && <span className="ml-2">· {zh ? "上次修改" : "Updated"} {cfg.updatedBy} {cfg.updatedAt.slice(0, 10)}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowSug((v) => !v)} data-testid="ps-suggest-toggle" className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border ${showSug ? "bg-sky-600 text-white border-sky-600" : "text-sky-700 border-sky-200 hover:bg-sky-50"}`}><Sparkles className="h-3.5 w-3.5" />{zh ? "实际耗时建议" : "Learned suggestions"}</button>
          {canEdit && <button onClick={resetAll} disabled={busy} data-testid="ps-reset-all" className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"><RotateCcw className="h-3.5 w-3.5" />{zh ? "恢复系统默认" : "System defaults"}</button>}
          {canEdit && <button onClick={save} disabled={busy || !dirty} data-testid="ps-save" className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-full px-3 py-1.5 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}{zh ? "保存" : "Save"}</button>}
        </div>
      </div>

      {showSug && <SuggestBar sug={sug} loading={sugBusy} months={months} setMonths={setMonths} zh={zh} canEdit={canEdit} onAdoptAll={adoptAll} adoptable={adoptable} />}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="ps-table">
          <thead>
            <tr className="text-[11px] text-slate-400 text-left border-b border-slate-100">
              <th className="px-3 py-2 font-medium">{zh ? "项目类型" : "Project type"}</th>
              {PHASES.map((ph, i) => <th key={ph.key} className="px-2 py-2 font-medium text-center">{i + 1}-{zh ? ph.zh : ph.en}<br /><span className="text-[10px] text-slate-300">{zh ? "天" : "days"}</span></th>)}
              <th className="px-3 py-2 font-medium text-center">{zh ? "总工期" : "Total"}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isDefault = r.key === "default";
              const custom = isDefault || !!draft.byType[r.key];
              const arr = arrOf(draft, r.key);
              const sr = showSug ? sugRows(r.key) : [];
              const rowAdoptable = sr.some((s) => s.suggested != null && s.suggested !== arr[s.idx]);
              return (
                <tr key={r.key} className={`border-b border-slate-50 ${isDefault ? "bg-slate-50/60" : ""}`} data-testid={`ps-row-${r.key}`}>
                  <td className="px-3 py-2">
                    <p className="font-semibold text-slate-800">{zh ? r.zh : r.en}</p>
                    {!isDefault && <p className="text-[10px] text-slate-400">{custom ? (zh ? "已单独设置" : "Custom") : (zh ? "跟随默认" : "Follows default")}</p>}
                  </td>
                  {arr.map((v, i) => (
                    <td key={i} className="px-1.5 py-1.5 text-center align-top">
                      <input type="number" min={1} max={365} value={v} disabled={!canEdit} onChange={(e) => setCell(r.key, i, e.target.value)} data-testid={`ps-cell-${r.key}-${i}`}
                        className={`w-14 h-8 text-center rounded-md border text-sm focus:outline-none focus:ring-1 focus:ring-brand-300 disabled:bg-slate-50 ${custom ? "border-slate-200 text-slate-800" : "border-dashed border-slate-200 text-slate-400"}`} />
                      {showSug && <SuggestBadge s={sr[i]} zh={zh} canEdit={canEdit} current={v} testid={`ps-adopt-${r.key}-${i}`} onAdopt={() => setCell(r.key, i, sr[i].suggested)} />}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-center font-bold text-slate-700" data-testid={`ps-total-${r.key}`}>{total(arr)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap space-x-2">
                    {showSug && canEdit && rowAdoptable && <button onClick={() => adoptRow(r.key)} data-testid={`ps-adopt-row-${r.key}`} className="text-[11px] text-sky-700 hover:underline">{zh ? "采纳本行" : "Adopt row"}</button>}
                    {!isDefault && canEdit && (custom
                      ? <button onClick={() => followDefault(r.key)} data-testid={`ps-use-default-${r.key}`} className="text-[11px] text-slate-500 hover:text-brand-500 hover:underline">{zh ? "改回跟随默认" : "Use default"}</button>
                      : <button onClick={() => copyDefault(r.key)} data-testid={`ps-customize-${r.key}`} className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-brand-500 hover:underline"><Copy className="h-3 w-3" />{zh ? "单独设置" : "Customize"}</button>)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">{zh ? `系统默认：${cfg.systemDefault.join(" / ")} 天（来自甘特模板，共 ${total(cfg.systemDefault)} 天）。各阶段开始日 = 开工日 + 前序阶段天数累计。` : `System default: ${cfg.systemDefault.join(" / ")} days (Gantt template, ${total(cfg.systemDefault)} days total). Phase start = start date + preceding phases.`}</p>
    </div>
  );
}
